/*
 * Route kinds of the new shell (plan-ds §4.3). Pure and tiny: Router.tsx imports this file eagerly (it is one
 * of the two shell modules allowed in the initial chunk, §4.2), so it imports nothing.
 *
 * Paths match with the same rule as `Router.matchRoute`: `^/?` + the route path with every `:param` replaced
 * by `([\w-]+)` + `$`. The path lists mirror routes.tsx, and each list says what the route ELEMENT draws with the new
 * UI on (review F08): a module list path is one whose element renders the `GridPageLayout variant="module"` branch
 * (the V2 module screen, §5.7); a module path whose element stays classic is a record page or a classic list and gets
 * `ClassicPageFrame`. One route decides by its query: `/reviews` is the project Reviews module screen with a
 * `projectId` (ReviewsList's `variant="module"` condition) and the classic portal list without one.
 */

export type ShellKind =
  "portal" | "projects" | "settings" | "project" | "psettings" | "module" | "estimate" | "viewer" | "other";

/** The classic tree's own guard around the routes (Router `getContent`): routes, Forbidden or the spinner. */
export type ShellGate = "ready" | "forbidden" | "loading";

function toMatcher(path: string): RegExp {
  return new RegExp("^/?" + path.replaceAll(/:[^\s/]+/g, String.raw`([\w-]+)`) + "$");
}

function matchers(paths: readonly string[]): readonly RegExp[] {
  return paths.map(toMatcher);
}

const PORTAL = matchers(["/dashboard"]);
const PROJECTS = matchers(["/projects"]);
const SETTINGS = matchers([
  "/portalSettings/:settingType",
  "/portalSettings/:settingType/:formId/:formCategory",
  "/portalSettings/:settingType/:costDatabaseId/costDatabaseVersion/:costDatabaseVersionId",
  "/portalSettings/:settingType/checklists/:checklistId",
  "/portalSettings/:settingType/template/:nomenclatureTemplateId",
  "/portalSettings/:settingType/:costDatabaseId",
  "/portalSettings/:settingType/:projectId/projectUsers",
  "/portalSettings/:settingType/:userId/userProjects",
  "/portalSettings/:settingType/:organisationId/orgUsers",
]);
const PROJECT = matchers(["/project/:projectId/projectdashboard"]);
const PSETTINGS = matchers(["/project/:projectId/setting"]);
/** Module lists whose element renders the V2 module screen. */
const MODULE_LISTS = matchers([
  "/project/:projectId/mails",
  "/project/:projectId/folders",
  "/project/:projectId/issues",
  "/project/:projectId/listTransmittals",
  "/project/:projectId/listFiles",
  "/project/:projectId/assetsList",
  "/project/:projectId/EstimateList",
  "/project/:projectId/listTask",
  "/project/:projectId/contractsList",
  "/project/:projectId/formsList/:moduleName",
  "/project/:projectId/scheduleList",
  "/project/:projectId/clashTests",
]);
/**
 * Module lists whose element has no module-screen branch and stays classic: Photos (`PhotosList`, an embedded
 * iframe) and the spaces list (`Workspaces` → `WorkspaceList`). Module kind (module crumb), classic body.
 */
const MODULE_CLASSIC_LISTS = matchers(["/project/:projectId/photos", "/project/:projectId/Workspaces"]);
/** Module record pages: module kind, classic body. */
const MODULE_DETAILS = matchers([
  "/project/:projectId/forms/:resourceId",
  "/project/:projectId/issues/:issueId",
  "/project/:projectId/listTask/:taskId",
  "/project/:projectId/listTransmittals/:transmittalVersionId",
  "/project/:projectId/contract/:contractId",
  "/project/:projectId/schedule/:scheduleId/ScheduleDetails",
  "/project/:projectId/schedule/:scheduleId/version/:versionId",
  "/project/:projectId/clashDetails/:clashTestId",
  "/project/:projectId/mails/:mailId/mailThread",
  "/project/:projectId/assetsList/:assetId/relatedDocuments",
]);
/**
 * The estimate record (plan-ds §4.15 R1, v3): its own kind (Estimates nav item, the estimate crumb). Its body follows
 * `ESTIMATE_V2_BODY` (below).
 */
const ESTIMATE = matchers([
  "/project/:projectId/estimate/:estimateId",
  "/project/:projectId/estimate/:estimateId/version/:versionId",
]);
/**
 * Whether the estimate route element renders the V2 estimate window (§5.17), which fills the main column itself and
 * brings a classic frame only for its gating states (`ModuleGateFrame`). The flag-on route element
 * (`Estimate.tsx`) renders `EstimateV2`, so the estimate body is not wrapped in `ClassicPageFrame`.
 */
export const ESTIMATE_V2_BODY: boolean = true;

const VIEWER = matchers(["/designviewer/file/:fileId"]);
/** The Reviews route: its kind follows the query (see the file comment). */
const REVIEWS = toMatcher("/reviews");

function matchesAny(pathname: string, list: readonly RegExp[]): boolean {
  return list.some((matcher) => matcher.test(pathname));
}

/**
 * `search` as `location.search` gives it (`?a=b`, or ""); a `pathname` that still carries its query (a nav path such
 * as `/reviews?projectId=…`) is split here, so both spellings classify alike.
 */
function splitQuery(pathname: string, search: string): { path: string; query: URLSearchParams } {
  const at = pathname.indexOf("?");
  if (at === -1) return { path: pathname, query: new URLSearchParams(search) };
  return { path: pathname.slice(0, at), query: new URLSearchParams(search === "" ? pathname.slice(at) : search) };
}

/** The project Reviews module screen: `/reviews` with a `projectId` and no `spaceId` (ReviewsList's condition). */
function isReviewsModule(path: string, query: URLSearchParams): boolean {
  return REVIEWS.test(path) && (query.get("projectId") ?? "") !== "" && (query.get("spaceId") ?? "") === "";
}

/** A module record page (issue, task, transmittal, contract, schedule, clash test, mail thread, asset, form). */
export function isModuleDetailPath(pathname: string): boolean {
  return matchesAny(splitQuery(pathname, "").path, MODULE_DETAILS);
}

/**
 * The shell kind of a route. Every path the table does not name is `other` (its route element renders inside
 * `ClassicPageFrame`). Whether the shell renders at all is Router's decision (`showNextShell`, §4.2): the viewer
 * kind applies only with the viewer flag, and `showHeader:false` routes never get the shell.
 */
export function getRouteKind(pathname: string, search = ""): ShellKind {
  const { path, query } = splitQuery(pathname, search);
  if (matchesAny(path, PORTAL)) return "portal";
  if (matchesAny(path, PROJECTS)) return "projects";
  if (matchesAny(path, SETTINGS)) return "settings";
  if (matchesAny(path, PROJECT)) return "project";
  if (matchesAny(path, PSETTINGS)) return "psettings";
  if (matchesAny(path, MODULE_LISTS) || matchesAny(path, MODULE_CLASSIC_LISTS) || matchesAny(path, MODULE_DETAILS)) {
    return "module";
  }
  if (isReviewsModule(path, query)) return "module";
  if (matchesAny(path, ESTIMATE)) return "estimate";
  if (matchesAny(path, VIEWER)) return "viewer";
  return "other";
}

/**
 * Bodies that stay classic inside the new chrome (§4.3 "body" column): `other` routes, module record pages, the
 * classic module lists and the estimate record while `ESTIMATE_V2_BODY` is off render their route element in
 * `ClassicPageFrame` (definite height, DS base rules off); every other kind's route element is a V2 body.
 */
export function isClassicBodyPath(pathname: string, search = "", estimateV2Body = ESTIMATE_V2_BODY): boolean {
  const kind = getRouteKind(pathname, search);
  if (kind === "other") return true;
  if (kind === "estimate") return !estimateV2Body;
  if (kind !== "module") return false;
  const { path } = splitQuery(pathname, search);
  return matchesAny(path, MODULE_DETAILS) || matchesAny(path, MODULE_CLASSIC_LISTS);
}

/** A route entry as routes.tsx declares it (only the fields read here; this file imports nothing). */
export interface RouteHeaderFlag {
  path: readonly string[];
  showHeader?: boolean;
}

const routeMatcherCache = new Map<string, RegExp>();

function cachedMatcher(path: string): RegExp {
  let matcher = routeMatcherCache.get(path);
  if (matcher === undefined) {
    matcher = toMatcher(path);
    routeMatcherCache.set(path, matcher);
  }
  return matcher;
}

/**
 * The current route's own `showHeader` flag, read in RENDER (Router's `showNextShell`, §4.2). `renderHeader`
 * mirrors the same flag one effect later (Router's route-flag effect dispatches `setRenderHeader` after the
 * navigation render), so a switch keyed on it lags: the new route renders once in the wrong tree (SplitLayout
 * vs AppShellNext) and is then remounted in the other. Same rule as that effect: the first route whose path
 * matches decides, and a path no route matches shows the header.
 */
export function routeShowsHeader(pathname: string, routes: readonly RouteHeaderFlag[]): boolean {
  return findRoute(pathname, routes)?.showHeader !== false;
}

/** The first route whose path matches `pathname` (the one Router renders), or undefined when none does. */
export function findRoute<R extends RouteHeaderFlag>(pathname: string, routes: readonly R[]): R | undefined {
  return routes.find((route) => route.path.some((path) => cachedMatcher(path).test(pathname)));
}
